import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    Button, Col, FormInput, Modal, ModalBody,
    ModalHeader, Row, SelectOption, Text, useUI
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { formatForSelect } from '@evertel/utils'
import { roles as roleOptions } from '@evertel/constants'
import { APIDataDepartmentAgreement, APIDataRoom } from '@evertel/types'
import { AgreementController } from '@evertel/agreements'
import { AgreementTextEditor, AGREEMENT_CONTENT_MAX_LENGTH } from '../elements/AgreementTextEditor'
import { AgreementSettingsFields } from '../elements/AgreementSettingsFields'

interface EditAgreementModalProps {
    visible: boolean
    agreement: APIDataDepartmentAgreement
    onSuccess?: () => void
    onClose: () => void
}

const EditAgreementModal: React.FC<EditAgreementModalProps> = observer(({
    visible,
    agreement,
    onSuccess,
    onClose
}) => {
    const sessionService = useService(SessionService, [])
    const agreementController = useService(AgreementController, [sessionService.selectedDepartmentId])
    const { addToast } = useUI()
    const [isEditingContent, setIsEditingContent] = useState(false)
    const [name, setName] = useState('')
    const [content, setContent] = useState('')
    const [contentLength, setContentLength] = useState(0)
    const [roles, setRoles] = useState<SelectOption[]>([])
    const [rooms, setRooms] = useState<APIDataRoom[]>([])
    const [roomsModified, setRoomsModified] = useState(false)
    const [acceptOncePerRoom, setAcceptOncePerRoom] = useState(false)
    const [isActive, setIsActive] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [nameError, setNameError] = useState('')

    const agreementId = agreement.id

    useEffect(() => {
        if (!visible || !agreementId) return

        agreementController.init(sessionService.selectedDepartmentId)

        const doc = agreement.currentDocument
        setName(doc?.name ?? agreement.title ?? '')
        setContent(doc?.content ?? '')
        setIsActive(agreement.enabled ?? true)
        setAcceptOncePerRoom(agreement.acceptOncePerRoom ?? false)
        setRoles(
            (agreement.restrictedRoles ?? []).map(r => roleOptions.find(o => o.value === r) ?? { value: r, label: r })
        )
        setRooms([])
        setRoomsModified(false)
        setIsEditingContent(false)
        setNameError('')

        const loadRooms = async () => {
            try {
                const assignments = await agreementController.fetchAssignments(agreementId)
                const roomIds = assignments
                    .filter(a => a.principalType?.toLowerCase() === 'room' && a.enabled && a.principalId !== undefined)
                    .map(a => a.principalId as number)

                if (roomIds.length) {
                    const rooms = await agreementController.fetchRoomNames(roomIds)
                    setRooms(formatForSelect(rooms, 'name', 'id', 'Unknown', 'id') as APIDataRoom[])
                }
            } catch (e: any) {
                addToast({ color: 'danger', title: 'Load Failed', message: e?.message || 'Failed to load agreement rooms' })
            }
        }

        loadRooms()
    }, [visible, agreementId])

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setName(value)
        setNameError(value.trim() ? '' : 'Agreement name is required')
    }

    const handleSave = async () => {
        if (!agreementId) return

        setIsSaving(true)

        try {
            if (isEditingContent) {
                await agreementController.updateContent(agreementId, {
                    name: name.trim(),
                    content
                })
            }

            const settingsPayload: {
                enabled?: boolean
                acceptOncePerRoom?: boolean
                restrictedRoles?: string[]
                roomIds?: number[]
            } = {
                enabled: isActive,
                acceptOncePerRoom,
                restrictedRoles: roles.map(r => r.value as string)
            }

            if (roomsModified) {
                settingsPayload.roomIds = rooms.filter(r => r.id !== undefined).map(r => r.id as number)
            }

            await agreementController.updateSettings(agreementId, settingsPayload)

            addToast({ color: 'success', title: 'Agreement Updated', message: 'Your changes have been saved.' })
            onSuccess?.()
            onClose()
        } catch (e: any) {
            addToast({ color: 'danger', title: 'Save Unsuccessful', message: e?.message || 'Failed to save agreement' })
        } finally {
            setIsSaving(false)
        }
    }

    const currentName = agreement.currentDocument?.name ?? agreement.title ?? ''

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            size="lg"
            backdrop="static"
        >
            <ModalHeader title="Edit Agreement" />
            <ModalBody className="mb-2">
                <Text bold className="d-block mb-3">Content</Text>
                {isEditingContent ? (
                    <>
                        <FormInput
                            id="edit-agreement-name"
                            name="name"
                            label="Agreement name"
                            required
                            value={name}
                            onChange={handleNameChange}
                            error={nameError}
                            placeholder="Agreement name"
                            className="mb-3"
                            maxLength={90}
                        />
                        <AgreementTextEditor
                            value={content}
                            onChange={setContent}
                            minHeight={200}
                            maxLength={AGREEMENT_CONTENT_MAX_LENGTH}
                            onLengthChange={setContentLength}
                        />
                    </>
                ) : (
                    <div className="border rounded p-3">
                        <Row valign="center" align="between">
                            <Col>
                                <Text bold className="d-block">{currentName}</Text>
                                <small className="text-muted">
                                    Editing the contents will create a new version and users will be prompted to accept it again.
                                </small>
                            </Col>
                            <Col xs="auto">
                                <Button
                                    color="link"
                                    size="md"
                                    onClick={() => setIsEditingContent(true)}
                                >
                                    <strong>Edit</strong>
                                </Button>
                            </Col>
                        </Row>
                    </div>
                )}

                <div className="border-top mt-4 pt-3">
                    <AgreementSettingsFields
                        departmentId={sessionService.selectedDepartmentId}
                        idPrefix="edit-agreement"
                        roles={roles}
                        onRolesChange={setRoles}
                        rooms={rooms}
                        onRoomsChange={(val) => {
                            setRooms(val)
                            setRoomsModified(true)
                        }}
                        acceptOncePerRoom={acceptOncePerRoom}
                        onAcceptOncePerRoomChange={setAcceptOncePerRoom}
                        enabled={isActive}
                        onEnabledChange={setIsActive}
                    />
                </div>

                <div className="d-flex justify-content-end mt-4">
                    <Button
                        color="link"
                        className="me-2"
                        onClick={onClose}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleSave}
                        loading={isSaving}
                        disabled={isSaving || (isEditingContent && (!name.trim() || contentLength > AGREEMENT_CONTENT_MAX_LENGTH))}
                    >
                        Save
                    </Button>
                </div>
            </ModalBody>
        </Modal>
    )
})

EditAgreementModal.displayName = 'EditAgreementModal'
export { EditAgreementModal }
